// src/pages/AdminAddons.jsx
import { useEffect, useMemo, useState } from 'react';

const fmt = (n) => {
  const num = parseFloat(n ?? 0);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString('no-NO');
};

export default function AdminAddons() {
  const API = process.env.REACT_APP_API_URL;

  // venstre panel (base-produkter)
  const [products, setProducts] = useState([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [selectedBase, setSelectedBase] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // høyre panel (relasjoner for base)
  const [rels, setRels] = useState([]);
  const [loadingRels, setLoadingRels] = useState(false);

  // “legg til child” form
  const [childId, setChildId] = useState('');
  const [saving, setSaving] = useState(false);

  // UI
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const token = localStorage.getItem('token');

  // === HENT PRODUKTER (venstre) ===
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoadingProducts(true);
      setError('');
      try {
        const res = await fetch(`${API}/products`);
        if (!res.ok) throw new Error('Kunne ikke hente produkter');
        const data = await res.json();
        if (!ignore) setProducts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoadingProducts(false);
      }
    })();
    return () => { ignore = true; };
  }, [API]);

  // === HENT RELASJONER (høyre) ===
  useEffect(() => {
    let ignore = false;
    setRels([]);
    setChildId('');
    setSuccess('');
    setError('');
    setConfirmId(null);
    if (!selectedBase?.id) return;

    (async () => {
      setLoadingRels(true);
      try {
        const res = await fetch(`${API}/addons?base_product_id=${selectedBase.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = res.ok ? await res.json() : [];
        if (!ignore) setRels(Array.isArray(data) ? data : []);
      } catch {
        if (!ignore) setError('Kunne ikke hente relasjoner');
      } finally {
        if (!ignore) setLoadingRels(false);
      }
    })();

    return () => { ignore = true; };
  }, [API, selectedBase?.id, token]);

  // === VENSTRE: filtrert liste ===
  const filteredLeft = useMemo(() => {
    const s = (searchLeft || '').toLowerCase();
    return (products || []).filter(p => !s || (p.name || '').toLowerCase().includes(s));
  }, [products, searchLeft]);

  // === HØYRE: kandidater i dropdown ===
  const usedChildIds = new Set(rels.map(r => r.product_id));
  const candidates = useMemo(() => {
    if (!selectedBase) return [];
    return (products || [])
      .filter(p => p.is_active)
      .filter(p => p.id !== selectedBase.id)
      .filter(p => !usedChildIds.has(p.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products, rels, selectedBase]);

  // === HÅNDTER: legg til relasjon ===
  async function addRelation(e) {
    e?.preventDefault?.();
    if (!selectedBase?.id || !childId) return;
    setError(''); setSuccess('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/addons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ base_product_id: selectedBase.id, product_id: Number(childId) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Feil ved lagring');
      setRels(prev => [...prev, data]); // NB: ingen await i callback
      setSuccess('Relasjon lagt til.');
      setChildId('');
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSaving(false);
    }
  }

  // === HÅNDTER: slett relasjon (inline bekreftelse) ===
  function askRemove(relId) {
    setConfirmId(relId);
    setSuccess('');
    setError('');
  }

  async function confirmRemove() {
    if (!confirmId) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API}/addons/${confirmId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Feil ved sletting');
      setRels(prev => prev.filter(r => r.id !== confirmId));
      setSuccess('Relasjon fjernet.');
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmId(null);
    }
  }

  function cancelRemove() {
    setConfirmId(null);
  }

  // fallback hvis backend ikke inkluderer as: 'addon'
  function resolveChild(rel) {
    return rel?.childProduct || products.find(p => p.id === rel.product_id) || {};
  }

  return (
    <div className="w-full min-h-[600px] flex gap-6">
      {/* VENSTRE: Produkter */}
      <div className="w-1/2 pr-6 border-r border-gray-200">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-2xl font-bold text-[#000099]">Relasjoner</h2>
        </div>

        <input
          type="text"
          placeholder="Søk i produkter..."
          value={searchLeft}
          onChange={e => setSearchLeft(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-gray-300 rounded focus:ring focus:ring-[#000099] text-sm"
        />

        {error && <p className="text-red-600 mb-2">{error}</p>}
        {success && <p className="text-green-600 mb-2">{success}</p>}

        {loadingProducts ? (
          <div className="text-sm text-gray-500">Laster produkter…</div>
        ) : filteredLeft.length === 0 ? (
          <div className="text-sm text-gray-500">Ingen produkter funnet.</div>
        ) : (
          <ul className="space-y-2 max-h-[520px] overflow-auto">
            {filteredLeft.map(p => (
              <li
                key={p.id}
                onClick={() => { setSelectedBase(p); setError(''); setSuccess(''); }}
                className={`p-3 border rounded cursor-pointer hover:bg-gray-100 transition
                  ${selectedBase?.id === p.id ? 'border-[#000099] bg-blue-50' : 'border-gray-100'}`}
              >
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-gray-500">
                  {p.is_for_sale ? `Kjøp: ${fmt(p.price_sale)} kr · ` : ''}
                  Leie: {fmt(p.price_day)} kr/døgn
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* HØYRE: Relasjoner for valgt base + legg til ny (dropdown) */}
      <div className="w-1/2 pl-6">
        {!selectedBase ? (
          <p className="text-gray-600">Velg et base-produkt til venstre.</p>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-[#000099] mb-3">
              Tillegg for: <span className="font-bold">{selectedBase.name}</span>
            </h3>

            {/* Eksisterende relasjoner */}
            {loadingRels ? (
              <div className="text-sm text-gray-500 mb-4">Laster relasjoner…</div>
            ) : (
              <div className="space-y-2 mb-6">
                {rels.length === 0 && (
                  <div className="text-sm text-gray-500">Ingen relasjoner opprettet ennå.</div>
                )}
                {rels.map(r => {
                  const p = resolveChild(r);
                  return (
                    <div key={r.id} className="flex items-center justify-between border rounded p-2">
                      <div className="flex items-center gap-3">
                        {p.image_url && (
                          <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                        )}
                        <div>
                          <div className="text-sm font-medium">{p.name || `#${p.id}`}</div>
                          <div className="text-xs text-gray-500">
                            {p.is_for_sale && p.price_sale ? `Kjøp ${fmt(p.price_sale)} kr · ` : ''}
                            {p.price_day ? `Leie ${fmt(p.price_day)} kr/døgn` : ''}
                          </div>
                        </div>
                      </div>

                      {confirmId === r.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={confirmRemove}
                            className="text-white bg-red-600 hover:bg-red-700 text-xs px-3 py-1 rounded"
                          >
                            Bekreft sletting
                          </button>
                          <button
                            onClick={cancelRemove}
                            className="text-xs px-3 py-1 rounded border hover:bg-gray-50"
                          >
                            Avbryt
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => askRemove(r.id)}
                          className="text-red-600 text-sm hover:underline"
                        >
                          Fjern
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Legg til ny relasjon (dropdown) */}
            <div className="bg-gray-50 border rounded p-4">
              <div className="font-medium mb-2">
                Legg til relasjon til <span className="font-semibold">{selectedBase.name}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Child-produkt</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={childId}
                    onChange={e => setChildId(e.target.value)}
                  >
                    <option value="">— Velg produkt —</option>
                    {candidates.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-1 text-right">
                  <button
                    onClick={addRelation}
                    disabled={!childId || saving}
                    className={`px-4 py-2 rounded text-white font-semibold ${
                      !childId || saving ? 'bg-blue-300 cursor-not-allowed' : 'bg-[#000099] hover:bg-blue-900'
                    }`}
                  >
                    {saving ? 'Legger til…' : 'Legg til'}
                  </button>
                </div>
              </div>

              {candidates.length === 0 && (
                <div className="text-xs text-gray-500 mt-2">
                  Ingen kandidater (alt er enten inaktivt, allerede knyttet eller er samme som base).
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
